import { useCallback } from 'react'
import { useAccessUser } from '@hook/useAccessUser'
import { useDisabledHover } from '@hook/useDisabledHover'
import { formatDate } from '@/utils/date'
import { TAB_SEARCH_DOCS_KEY, TAB_SEARCH_INDEX_KEY } from './useCollectionViewModel'
import cn from 'clsx'
import { parseDate } from '@/utils/date'


import { TextInput } from '@components/shared/TextInput'

import Flatpickr from 'react-flatpickr'

const SearchFormSidebar = ({
    className,
    activeTab,
    handleKeyUp,
    handleChange,
    
    // errorShow,
    // setErrorShow,
    // showErrorPopup,
}) => {
    
    const documentBookPage = useAccessUser({
        accessType: 'specific_collection',
        accessName: 'document_search_index',
    })
    const documentNameChange = useAccessUser({
        accessType: 'specific_collection',
        accessName: 'document_search_full_name',
    })
    const documentPropertyDescription = useAccessUser({
        accessType: 'specific_collection',
        accessName: 'document_search_legal_description',
    })
    const documentSearchParams = useDisabledHover(!documentPropertyDescription)
    const documentPage = useDisabledHover(!documentBookPage)
    
    const createInputRowInput = useCallback((value, label, props = {}) => (
        <TextInput
            className={props?.className ?? 'mb-0'}
            labelClassName="block text-sm"
            inputClassName="input input-bordered input-sm w-full"
            name={value}
            onKeyUp={e => handleKeyUp(e)}
            handleChange={handleChange}
            label={label}
            {...props} />
    ), [handleChange])
    
    const createInputRow = useCallback((value, label, props = {}, parentProps = {}) => (
        <div className="mb-2" {...parentProps}>
            {createInputRowInput(value, label, props)}
        </div>
    ), [handleChange])
    
    const createDateInput = (field, props) => (
        <div className={cn('', props?.className)}>
            <Flatpickr
                className={cn('mb-0 flatpickr-input input input-bordered input-sm w-full')}
                name={field}
                placeholder={props?.placeholder ?? ''}
                autoComplete="off"
                options={{
                    disableMobile: true,
                    closeOnSelect: true,
                    allowInput: true,
                    dateFormat: 'm/d/Y',
                    parseDate: date => parseDate(date, null, false),
                }}
                onBlur={ev => {
                    // necessary to force the changes when user types the date.
                    // somehow flatpickr doesn't trigger onchange in this scenario
                    const fp = ev.target._flatpickr
                    
                    if (!fp.innerContainer.parentElement.contains(ev.relatedTarget))
                        handleChange({ target: {
                            name: field,
                            value: formatDate(fp.selectedDates[0], 'YYYY-MM-DD'),
                        } })
                }}
                onChange={date => {
                    // i have to simulate an event in order to set on formik data
                    handleChange({ target: {
                        name: field,
                        value: formatDate(date[0], 'YYYY-MM-DD'),
                    } })
                }} />
        </div>
    ) 
    
    return (
        
        <div className={cn('SearchFormSidebar', className)}>
            
            {activeTab === TAB_SEARCH_DOCS_KEY && (<>
                <div className="mb-3">
                    <div>
                        {createInputRow('name', 'Document Name', { disabled: !documentNameChange })}
                        <div className="mb-2">
                            {createInputRowInput('state', 'State', { className: 'mb-0 col-sm-6' })}
                            {createInputRowInput('county', 'County', { className: 'mb-0 pl-0 col-sm-6' })}
                        </div>
                        {createInputRow('grantorGrantee', 'Grantor/Grantee', { disabled: !documentNameChange })}
                        {createInputRow('grantor', 'Grantor')}
                        {createInputRow('grantee', 'Grantee')}
                        {createInputRow('volume', 'Book/Volume')}
                        {createInputRow('page', 'Page', { disabled: !documentBookPage }, documentPage)}
                        {createInputRow('bookType', 'Book Type')}
                        {createInputRow('instrumentNumber', 'Instrument #')}
                        {createInputRow('instrumentType', 'Instrument Type')}
                    </div>
                </div>
                
                <div>
                    <label className="text-sm">File Date</label>
                    <div className="mb-3">
                        {createDateInput('fileDateFrom', { placeholder: 'From' })}
                        {createDateInput('fileDateTo', { className: 'pl-0', placeholder: 'To' })}
                    </div>
                    <label className="text-sm">Instrument Date</label>
                    <div className="row">
                        {createDateInput('instrumentDateFrom', { placeholder: 'From' })}
                        {createDateInput('instrumentDateTo', { className: 'pl-0', placeholder: 'To' })}
                    </div>
                </div>
                
                <div className="mb-3 SearchFormSidebar-property-description card">
                    
                    <div className="p-3 card-header">
                        Property Description
                    </div>
                    
                    <div>
                        
                        {createInputRow('section', 'Section', { 
                            className: 'mb-0 col-sm-6',
                            disabled: !documentPropertyDescription,
                        }, documentSearchParams)}
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('block', 'Block', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('township', 'Township', {
                                className: 'mb-0 pl-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('survey', 'Survey', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('abstract', 'Abstract', {
                                className: 'mb-0 pl-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        {createInputRow('subdivision', 'Subdivision',
                            { disabled: !documentPropertyDescription },
                            documentSearchParams)}
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('subdivisionLot', 'Subdivision Lot', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('subdivisionBlock', 'Subdivision Block', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('minAcreage', 'Min Acreage', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('maxAcreage', 'Max Acreage', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                    
                    </div>
                
                </div>
            
            </>)}
            
            {activeTab === TAB_SEARCH_INDEX_KEY && (<>
                <div className="mb-3 SearchFormSidebar-index-fields card">
                    
                    {/* <div className="p-3 card-header">
                        Index Data
                    </div> */}
                    
                    {/*
                    countyId
                    documentNumber
                    bookVolume
                    bookPage
                    documentType
                    */}
                    
                    {/*
                    2023-05-08
                    Hid county from index search for now, since you're already searching
                    documents within a county. But don't delete, since we might
                    bring it back once we get full text / ElasticSearch integrated
                    */}
                    {/* <div className=">
                        {createInputRow('county', 'County')}
                    </div> */}
                    
                    <div>
                        {createInputRow('grantorGrantee', 'Grantor/Grantee', { disabled: !documentNameChange })}
                        {createInputRow('grantor', 'Grantor')}
                        {createInputRow('grantee', 'Grantee')}
                    </div>
                    
                    <div>
                        {createInputRow('documentNumber', 'Instrument #')}
                        {createInputRow('documentType', 'Instrument Type', { 
                            placeholder: 'Lien, transfer, deed...',
                        })}
                    </div>
                    
                    <div>
                        {createInputRow('bookType', 'Book Type')}
                        {createInputRow('bookVolume', 'Book Volume')}
                        {createInputRow('bookPage', 'Book Page')}
                    </div>
                    
                    <div>
                        <label className="text-sm">File Date</label>
                        <div className="flex gap-2">
                            {createDateInput('fileDateFrom', { placeholder: 'From' })}
                            {createDateInput('fileDateTo', { className: 'pl-0', placeholder: 'To' })}
                        </div>
                    </div>
                    
                    <div className="my-3">
                        <label className="text-sm">Instrument Date</label>
                        <div className="flex gap-2">
                            {createDateInput('instrumentDateFrom', { placeholder: 'From' })}
                            {createDateInput('instrumentDateTo', { className: 'pl-0', placeholder: 'To' })}
                        </div>
                    </div>
                    
                    <div>
                        {createInputRow('allLegalDescriptions', 'Legal Description')}
                    </div>
                    
                    {/* hiding block below as we don't have those info yet */}
                    {/*<div>
                        
                        {createInputRow('section', 'Section', { 
                            className: 'mb-0 col-sm-6',
                            disabled: !documentPropertyDescription,
                        }, documentSearchParams)}
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('block', 'Block', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('township', 'Township', {
                                className: 'mb-0 pl-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('survey', 'Survey', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('abstract', 'Abstract', {
                                className: 'mb-0 pl-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        {createInputRow('subdivision', 'Subdivision',
                            { disabled: !documentPropertyDescription },
                            documentSearchParams)}
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('subdivisionLot', 'Subdivision Lot', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('subdivisionBlock', 'Subdivision Block', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                        
                        <div className="mb-2" {...documentSearchParams}>
                            {createInputRowInput('subdivisionPage', 'Subdivision Page', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                            {createInputRowInput('subdivisionCabinet', 'Subdivision Cabinet', {
                                className: 'mb-0 col-sm-6',
                                disabled: !documentPropertyDescription,
                            })}
                        </div>
                    
                    </div>*/}
                
                </div>
            </>)}
            
            {/* {showErrorPopup && (
                <CustomModal
                    open={errorShow}
                    modalHandler={() => setErrorShow(!errorShow)}
                    center>
                    <div className="modal-body">
                        <div className="mt-2 justify-center items-center">
                            <div>
                                <h3 className="p-3">
                                    From and To dates are not in chronological order. Please
                                    update.
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="mx-8 btn btn-primary"
                                onClick={() => setErrorShow(false)}>
                                Dismiss
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )} */}
        
        </div>
        
    )
    
}

export default SearchFormSidebar
